
function lievreShippingThreshold(element) {
 const currency = element.dataset.currency;
 const rate = currency === element.dataset.storeCurrency ? 1 : Number(window.Shopify?.currency?.rate);
 if (element.dataset.storeCurrency !== 'CHF' || !Number.isFinite(rate) || rate <= 0) return null;
 return Math.ceil(Number(element.dataset.threshold) * rate / 100) * 100;
}
class LievreCurrency extends HTMLElement {
 connectedCallback() {
  this.toggle = this.querySelector('.lr-currency-toggle');
  this.list = this.querySelector('.lr-currency-list');
  this.controller = new AbortController();
  const options = {signal:this.controller.signal};
  this.toggle.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'), options);
  this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && matchMedia('(hover:hover)').matches && !this.contains(document.activeElement)) this.setOpen(false); }, options);
  this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.setOpen(false); }, options);
  this.addEventListener('keydown', event => {
   if (event.key === 'Escape') { this.setOpen(false); this.toggle.focus(); }
   if (event.key === 'ArrowDown' && event.target === this.toggle) { event.preventDefault(); this.setOpen(true); this.list.querySelector('button')?.focus(); }
  }, options);
  this.list.addEventListener('click', event => {
   const more = event.target.closest('[data-more-toggle]');
   if (more) {
    const expanded = more.getAttribute('aria-expanded') !== 'true';
    more.setAttribute('aria-expanded', String(expanded));
    more.textContent = expanded ? 'Less' : 'More';
    this.querySelector('[data-more-options]').hidden = !expanded;
    return;
   }
   const option = event.target.closest('button[data-country]');
   if (!option) return;
   const form = option.closest('form');
   form.querySelector('[name="country_code"]').value = option.dataset.country;
   // Shared Shopify previews do not retain market selection. Use the storefront
   // localization endpoint while keeping the same unpublished theme and page.
   const destination = new URL(window.location.href);
   destination.searchParams.delete('country');
   if (destination.hostname.endsWith('.shopifypreview.com')) {
    const storefront = new URL(this.dataset.storeUrl);
    form.action = new URL(form.getAttribute('action'), storefront.origin).href;
    destination.searchParams.set('preview_theme_id', this.dataset.themeId);
   }
   const returnTo = form.querySelector('[name="return_to"]');
   if (returnTo) returnTo.value = destination.pathname + destination.search + destination.hash;
   form.submit();
  }, options);
  document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.setOpen(false); }, options);
 }
 setOpen(open) {
  this.toggle.setAttribute('aria-expanded', String(open));
  this.toggleAttribute('data-open', open);
  this.list.inert = !open;
  if (!open) {
   const more = this.querySelector('[data-more-toggle]');
   if (more) { more.setAttribute('aria-expanded','false'); more.textContent = 'More'; this.querySelector('[data-more-options]').hidden = true; }
  }
 }
 disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('lr-currency')) customElements.define('lr-currency', LievreCurrency);

function lievreMoney(amount, currency, locale) {
 return new Intl.NumberFormat(locale || 'en', {style:'currency',currency,currencyDisplay:'narrowSymbol'}).format(amount / 100);
}
class LievreMoney extends HTMLElement {
 connectedCallback() { this.textContent = lievreMoney(Number(this.dataset.amount), this.dataset.currency, this.dataset.locale); }
}
if (!customElements.get('lr-money')) customElements.define('lr-money', LievreMoney);

class LievreFreeShipping extends HTMLElement {
 connectedCallback() {
  const currency = this.dataset.currency;
  const total = Number(this.dataset.total);
  const rate = currency === this.dataset.storeCurrency ? 1 : Number(window.Shopify?.currency?.rate);
  const message = this.querySelector('.lr-shipping-message');
  const track = this.querySelector('.lr-shipping-track');
  const left = this.querySelector('.lr-shipping-left');
  if (this.dataset.storeCurrency !== 'CHF' || !Number.isFinite(rate) || rate <= 0) {
   message.textContent = 'Free shipping from CHF 100.';
   left.textContent = '';
   track.hidden = true;
   return;
  }
  const threshold = lievreShippingThreshold(this);
  const remaining = Math.max(0, threshold - total);
  const progress = Math.min(100, Math.max(0, total / threshold * 100));
  message.textContent = remaining > 0 ? 'Free shipping from ' + new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency',currency,currencyDisplay:'narrowSymbol',minimumFractionDigits:0,maximumFractionDigits:0}).format(threshold / 100) + '.' : 'Free shipping unlocked.';
  left.textContent = remaining > 0 ? lievreMoney(remaining,currency,this.dataset.locale) + ' left' : lievreMoney(0,currency,this.dataset.locale) + ' left'; left.hidden = false;
  track.hidden = false;
  track.setAttribute('aria-valuenow', String(Math.round(progress)));
  track.querySelector('.lr-shipping-fill').style.width = progress + '%';
 }
}
if (!customElements.get('lr-free-shipping')) customElements.define('lr-free-shipping', LievreFreeShipping);

class LievreProductPurchase extends HTMLElement {
 connectedCallback() {
  this.product = this.closest('product-info');
  this.sticky = this.querySelector('.lr-mobile-add');
  // Shopify rich text can leave blank spacer paragraphs after the accordions.
  const description = this.product.querySelector('.product__description');
  for (const node of [...(description?.children || [])].reverse()) {
   if (node.tagName !== 'P' || node.textContent.trim() || node.querySelector('img,video,iframe,a,button,input')) break;
   node.classList.add('lr-description-spacer');
  }
  this.clickHandler = () => {
   const restock = this.product.querySelector('lr-restock[data-sold-out]');
   if (restock) { restock.open(); return; }
   const native = this.product.querySelector('product-form button[name="add"]');
   if (native && !native.disabled && native.getAttribute('aria-disabled') !== 'true') native.click();
  };
  this.sticky.addEventListener('click', this.clickHandler);
  this.observer = new MutationObserver(() => {
   if (this.frame) return;
   this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
  });
  this.observer.observe(this.product, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-disabled','data-amount']});
  this.sync();
  this.dockHandler = () => {
   if (this.dockFrame) return;
   this.dockFrame = requestAnimationFrame(() => { this.dockFrame = null; this.updateDock(); });
  };
  window.addEventListener('scroll', this.dockHandler, {passive:true});
  window.addEventListener('resize', this.dockHandler);
  this.layoutObserver = new ResizeObserver(this.dockHandler);
  this.layoutObserver.observe(this.product);
  this.updateDock();
 }
 updateDock() {
  const native = this.product.querySelector('product-form button[name="add"]');
  if (!native) return;
  const control = this.product.querySelector('lr-restock[data-sold-out] .lr-restock-trigger') || native;
  const docked = control.getBoundingClientRect().bottom <= window.innerHeight;
  const bar = this.querySelector('.lr-mobile-purchase');
  if (bar.hidden !== docked) bar.hidden = docked;
 }
 sync() {
  const native = this.product.querySelector('product-form button[name="add"]');
  const source = this.product.querySelector('[data-lr-variant-price]');
  if (!native) { this.sticky.disabled = true; return; }
  const soldOut = !!this.product.querySelector('lr-restock[data-sold-out]');
  const busy = !soldOut && (native.disabled || native.getAttribute('aria-disabled') === 'true');
  if (this.sticky.disabled !== busy) this.sticky.disabled = busy;
  const error = this.product.querySelector('.product-form__error-message');
  const errorTarget = this.querySelector('.lr-mobile-error');
  const errorText = error?.closest('[hidden]') ? '' : error?.textContent.trim() || '';
  if (errorTarget && errorTarget.textContent !== errorText) errorTarget.textContent = errorText;
  const nativeLabel = native.querySelector('span');
  if (!busy && this.dataset.preorder === 'true' && nativeLabel.textContent.trim() !== 'Pre-order') nativeLabel.textContent = 'Pre-order';
  this.sticky.classList.toggle('lr-mobile-add--restock',soldOut);
  const label = soldOut ? 'SOLD OUT - Notify me when available' : nativeLabel.textContent.trim();
  const target = this.querySelector('[data-lr-purchase-label]');
  if (target.textContent !== label) target.textContent = label;
  const price = source ? lievreMoney(Number(source.dataset.amount), this.dataset.currency, this.dataset.locale) : '';
  const targets = [this.product.querySelector('[data-lr-heading-price]')];
  targets.forEach(node => { if (node && node.textContent !== price) node.textContent = price; });
  const unavailable = /unavailable/i.test(label);
  targets.forEach(node => { if (node && node.hidden !== unavailable) node.hidden = unavailable; });
 }
 disconnectedCallback() {
  this.observer?.disconnect();
  this.layoutObserver?.disconnect();
  window.removeEventListener('scroll', this.dockHandler);
  window.removeEventListener('resize', this.dockHandler);
  if (this.dockFrame) cancelAnimationFrame(this.dockFrame);
  if (this.frame) cancelAnimationFrame(this.frame);
  this.sticky?.removeEventListener('click', this.clickHandler);
 }
}
if (!customElements.get('lr-product-purchase')) customElements.define('lr-product-purchase', LievreProductPurchase);

class LievreGalleryIndicator extends HTMLElement {
 connectedCallback() {
  this.slider = this.closest('slider-component').querySelector('.product__media-list');
  this.fill = this.querySelector('span');
  this.handler = () => {
   if (this.frame) return;
   this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
  };
  this.slider.addEventListener('scroll',this.handler,{passive:true});
  this.resizeObserver = new ResizeObserver(this.handler);
  this.resizeObserver.observe(this.slider);
  this.mediaObserver = new MutationObserver(this.handler);
  this.mediaObserver.observe(this.slider,{childList:true});
  this.update();
 }
 update() {
  const count = Array.from(this.slider.children).filter(item => item.getBoundingClientRect().width > 0).length;
  this.hidden = count <= 1;
  const width = 100 / Math.max(1,count);
  const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
  const fraction = maxScroll > 0 ? Math.min(1,Math.max(0,this.slider.scrollLeft / maxScroll)) : 0;
  this.fill.style.width = width + '%';
  this.fill.style.transform = 'translateX(' + fraction * Math.max(0,count - 1) * 100 + '%)';
 }
 disconnectedCallback() {
  this.slider?.removeEventListener('scroll',this.handler);
  this.resizeObserver?.disconnect();
  this.mediaObserver?.disconnect();
  if (this.frame) cancelAnimationFrame(this.frame);
 }
}
if (!customElements.get('lr-gallery-indicator')) customElements.define('lr-gallery-indicator',LievreGalleryIndicator);

class LievreProductRecommendations extends HTMLElement {
 async connectedCallback() {
  this.controller = new AbortController();
  let source = this;
  try {
   // Shopify orders this collection by actual best-selling performance.
   const response = await fetch(this.dataset.url, {signal:this.controller.signal});
   if (!response.ok) throw new Error('Recommendation response unavailable');
   const html = new DOMParser().parseFromString(await response.text(), 'text/html');
   const fetched = html.querySelector('lr-product-recommendations');
   if (fetched?.querySelector('template')) source = fetched;
  } catch (error) {
   if (error.name === 'AbortError') return;
   // Existing collection cards remain a useful fallback if the request fails.
  }
  if (!this.isConnected) return;
  const normalize = value => (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim();
  const families = value => {
   const names = {
    blue:/blue|navy|indigo|bleu|turquoise|teal|cyan|azure/,
    white:/white|ivory|cream|ecru|blanc/,
    black:/black|noir/,
    grey:/grey|gray|charcoal|gris/,
    pink:/pink|rose|fuchsia/,
    red:/red|burgundy|rouge/,
    green:/green|olive|vert/,
    yellow:/yellow|jaune/,
    brown:/brown|beige|tan|marron/,
    purple:/purple|violet|lavender/,
    orange:/orange|coral/
   };
   return Object.keys(names).filter(name => names[name].test(value));
  };
  const colour = normalize(this.dataset.colour);
  const currentFamilies = families(colour);
  const candidates = Array.from(source.querySelectorAll('template[data-product-id]'))
   .filter(card => card.dataset.productId !== this.dataset.productId);
  const score = card => {
   const candidateColour = normalize(card.dataset.colour);
   if (colour && colour === candidateColour) return 0;
   if (currentFamilies.some(family => families(candidateColour).includes(family))) return 1;
   return 2;
  };
  // Stable ordering retains best-selling order within each matching tier.
  const ranked = candidates.map((card,index) => ({card,index,score:score(card)}))
   .sort((a,b) => a.score - b.score || a.index - b.index);
  const grid = this.querySelector('.lr-recommendation-grid');
  grid.replaceChildren();
  const seen = new Set();
  for (const {card} of ranked) {
   if (seen.has(card.dataset.productId)) continue;
   seen.add(card.dataset.productId);
   grid.appendChild(document.importNode(card.content,true));
   if (seen.size === 4) break;
  }
  this.hidden = seen.size === 0;
 }
 disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('lr-product-recommendations')) customElements.define('lr-product-recommendations',LievreProductRecommendations);

/* A scoped year index; native editor selection reveals the matching project. */
class LievreProjects extends HTMLElement {
 connectedCallback() {
  this.selectedByYear = new Map();
  this.yearClick = event => {
   const project = event.target.closest('[data-project-select]');
   if (project && this.contains(project)) { this.showProject(project.dataset.projectSelect); return; }
   const year = event.target.closest('[data-project-year]');
   if (year && this.contains(year)) this.showYear(year.dataset.projectYear);
  };
  this.editorSelect = event => {
   const block = event.target.closest('[data-project-block]');
   if (block && this.contains(block)) this.showProject(block.dataset.projectBlock);
  };
  this.addEventListener('click',this.yearClick);
  this.addEventListener('shopify:block:select',this.editorSelect);
  this.classList.add('is-enhanced');
  this.showYear(this.querySelector('[data-project-year]')?.dataset.projectYear);
 }
 showYear(year) {
  const choices = [...this.querySelectorAll('[data-project-select]')].filter(button => button.dataset.projectIndexYear === year);
  const remembered = this.selectedByYear.get(year);
  const button = choices.find(button => button.dataset.projectSelect === remembered) || choices[0];
  if (button) this.showProject(button.dataset.projectSelect);
  else {
   this.querySelectorAll('[data-project-year]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.projectYear === year)));
   this.querySelectorAll('[data-project-panel]').forEach(panel => { panel.hidden = panel.dataset.projectPanel !== year; });
   this.querySelectorAll('[data-project-block]').forEach(article => { article.hidden = true; });
   this.querySelectorAll('[data-project-select]').forEach(button => button.setAttribute('aria-pressed','false'));
  }
 }
 showProject(id) {
  const article = [...this.querySelectorAll('[data-project-block]')].find(node => node.dataset.projectBlock === id);
  if (!article) return;
  const year = article.closest('[data-project-panel]').dataset.projectPanel;
  this.selectedByYear.set(year,id);
  this.querySelectorAll('[data-project-year]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.projectYear === year)));
  this.querySelectorAll('[data-project-panel]').forEach(panel => { panel.hidden = panel.dataset.projectPanel !== year; });
  this.querySelectorAll('[data-project-block]').forEach(node => { node.hidden = node !== article; });
  this.querySelectorAll('[data-project-select]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.projectSelect === id)));
 }
 disconnectedCallback() {
  this.removeEventListener('click',this.yearClick);
  this.removeEventListener('shopify:block:select',this.editorSelect);
 }
}
if (!customElements.get('lievre-projects')) customElements.define('lievre-projects', LievreProjects);

/* Small catalogue filters, using product metadata rather than descriptions. */
class LievreBoutique extends HTMLElement {
 async connectedCallback() {
  this.abort = new AbortController();
  this.grid = this.querySelector('.product-grid');
  this.controls = this.querySelector('.lr-boutique-filters');
  this.colourSelect = this.querySelector('[data-colour]');
  this.onClick = event => {
   const colourOption = event.target.closest('[data-colour-option]');
   if (colourOption) { this.colourSelect.value = colourOption.dataset.colourOption; this.applyFilters(true); this.closeColours(); this.querySelector('[data-colour-menu] summary').focus({preventScroll:true}); }
   const category = event.target.closest('[data-category]');
   if (category) { this.category = category.dataset.category; this.applyFilters(true); }
   if (event.target.closest('[data-reset]')) {
    this.category = 'all'; this.colourSelect.value = 'all'; this.applyFilters(true);
   }
  };
  this.onOutside = event => { if (!this.querySelector('[data-colour-menu]').contains(event.target)) this.closeColours(); };
  this.onEscape = event => { if (event.key === 'Escape' && this.querySelector('[data-colour-menu]').open) { this.closeColours(); this.querySelector('[data-colour-menu] summary').focus({preventScroll:true}); } };
  this.onEditorSelect = event => { if (event.target.closest('[data-boutique-editorial]')) { this.category = 'all'; this.colourSelect.value = 'all'; this.applyFilters(false); } };
  this.onHistory = () => { this.readFilters(); this.applyFilters(false); };
  try {
   await this.loadRemainingPages();
   if (!this.isConnected) return;
   const ordered = [...this.querySelectorAll('[data-boutique-product]')].sort((a,b) => Number(a.dataset.productGroup) - Number(b.dataset.productGroup));
   ordered.forEach(element => this.grid.append(element));
   this.products = ordered.map(element => this.productMetadata(element));
   this.editorials = [...this.querySelectorAll('[data-boutique-editorial]')];
   this.editorials.forEach(element => this.grid.insertBefore(element, ordered[Number(element.dataset.after)] || null));
   const colours = [...new Set(this.products.flatMap(product => product.colours))].sort((a,b) => a.localeCompare(b));
   colours.forEach(colour => {
    const option = document.createElement('button');
    option.type = 'button'; option.dataset.colourOption = colour; option.textContent = colour;
    option.setAttribute('aria-pressed','false');
    this.querySelector('[data-colour-options]').append(option);
   });
   this.querySelector('[data-category="bottoms"]').hidden = !this.products.some(product => product.category === 'bottoms');
   this.readFilters();
   this.controls.hidden = false;
   this.controls.addEventListener('click', this.onClick);
   document.addEventListener('click',this.onOutside);
   this.addEventListener('keydown',this.onEscape);
   this.addEventListener('shopify:block:select',this.onEditorSelect);
   window.addEventListener('popstate', this.onHistory);
   this.querySelector('[data-boutique-pagination]')?.setAttribute('hidden','');
   this.applyFilters(false);
  } catch (error) {
   if (error.name !== 'AbortError') this.querySelector('[data-filter-status]').textContent = 'All products shown. Filters are temporarily unavailable.';
  }
 }
 async loadRemainingPages() {
  let next = this.querySelector('[data-boutique-next]')?.getAttribute('href');
  const seen = new Set();
  while (next && !seen.has(next)) {
   seen.add(next);
   const url = new URL(next, location.href);
   url.searchParams.set('section_id', this.dataset.sectionId);
   const response = await fetch(url, {signal:this.abort.signal});
   if (!response.ok) throw new Error('Catalogue unavailable');
   const doc = new DOMParser().parseFromString(await response.text(),'text/html');
   const section = doc.querySelector('lievre-boutique');
   if (!section) throw new Error('Catalogue missing');
   const existing = new Set([...this.grid.children].map(item => item.dataset.boutiqueProduct));
   section.querySelectorAll('[data-boutique-product]').forEach(item => {
    if (!existing.has(item.dataset.boutiqueProduct)) this.grid.append(document.importNode(item,true));
   });
   next = section.querySelector('[data-boutique-next]')?.getAttribute('href');
  }
 }
 productMetadata(element) {
  const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim();
  let tags = [];
  try { tags = JSON.parse(element.dataset.productTags).map(normalize); } catch {}
  let category = ['tops','accessories','bottoms'].find(value => tags.includes('filter-' + value));
  if (!category) {
   const type = normalize(element.dataset.productType);
   const title = normalize(element.dataset.productTitle);
   const classify = value => {
    if (/bottom|shorts|trousers|pants|leggings/.test(value)) return 'bottoms';
    if (/accessor|cap|hat|bandana|gavroche|sock|glove/.test(value)) return 'accessories';
    if (/tops|shirt|tee|long sleeve|tank|singlet|hoodie|sweatshirt|\bls\b|\bss\b/.test(value)) return 'tops';
   };
   category = classify(type) || classify(title) || 'other';
  }
  const families = [
   ['Blue',/blue|navy|indigo|turquoise|teal|cyan/],
   ['White',/white|ivory|cream|ecru|off.white/],
   ['Grey',/grey|gray|silver/],
   ['Black',/black|noir/],
   ['Pink',/pink|rose|fuchsia/],
   ['Red',/red|burgundy|bordeaux|maroon/],
   ['Green',/green|olive|sage|khaki/],
   ['Yellow',/yellow|mustard/],
   ['Brown',/brown|tan|beige|sand|camel|earth/],
   ['Purple',/purple|lavender|lilac|violet/],
   ['Orange',/orange|salmon|peach/],
   ['Multicolour',/multi/]
  ];
  const group = text => {
   const normalized = normalize(text);
   const match = families.find(([,pattern]) => pattern.test(normalized));
   return match ? match[0] : text.trim().replace(/\b\w/g, char => char.toUpperCase());
  };
  const overrides = tags.filter(tag => tag.startsWith('color-')).map(tag => group(tag.slice(6).replaceAll('-',' '))).filter(Boolean);
  const colour = element.dataset.colourName.trim();
  return {element,category,colours:overrides.length ? [...new Set(overrides)] : colour ? [group(colour)] : []};
 }
 readFilters() {
  const params = new URL(location.href).searchParams;
  this.category = params.get('lr_type') || 'all';
  if (!['all','tops','accessories','bottoms'].includes(this.category)) this.category = 'all';
  this.colourSelect.value = params.get('lr_colour') || 'all';
  if (![...this.querySelectorAll('[data-colour-option]')].some(button => button.dataset.colourOption === this.colourSelect.value)) this.colourSelect.value = 'all';
 }
 applyFilters(updateUrl) {
  const colour = this.colourSelect.value;
  this.querySelector('[data-colour-label]').textContent = colour === 'all' ? 'Colour' : colour;
  this.querySelector('[data-colour-menu] summary').setAttribute('aria-label', 'Colour filter: ' + (colour === 'all' ? 'All' : colour));
  this.querySelectorAll('[data-colour-option]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.colourOption === colour)));
  this.editorials?.forEach(element => { element.hidden = this.category !== 'all' || colour !== 'all'; });
  let count = 0;
  this.products.forEach(product => {
   const visible = (this.category === 'all' || product.category === this.category) && (colour === 'all' || product.colours.includes(colour));
   product.element.hidden = !visible;
   if (visible) count++;
  });
  this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === this.category)));
  this.querySelector('[data-reset]').hidden = this.category === 'all' && colour === 'all';
  this.querySelector('[data-filter-empty]').hidden = count > 0;
  this.querySelector('[data-filter-status]').textContent = count + (count === 1 ? ' piece shown' : ' pieces shown');
  if (updateUrl) {
   const url = new URL(location.href);
   this.category === 'all' ? url.searchParams.delete('lr_type') : url.searchParams.set('lr_type',this.category);
   colour === 'all' ? url.searchParams.delete('lr_colour') : url.searchParams.set('lr_colour',colour);
   history.replaceState(history.state,'',url);
  }
 }
 closeColours() { this.querySelector('[data-colour-menu]').open = false; }
 disconnectedCallback() {
  this.abort?.abort();
  this.controls?.removeEventListener('click', this.onClick);
  document.removeEventListener('click',this.onOutside);
  this.removeEventListener('keydown',this.onEscape);
  this.removeEventListener('shopify:block:select',this.onEditorSelect);
  window.removeEventListener('popstate', this.onHistory);
 }
}
if (!customElements.get('lievre-boutique')) customElements.define('lievre-boutique', LievreBoutique);

const lievrePairCatalogCache = new Map();
async function lievrePairCatalog(url) {
 const cached = lievrePairCatalogCache.get(url);
 if (cached && Date.now() - cached.time < 60000) return cached.promise;
 const promise = (async () => {
  let next = url;
  const seen = new Set();
  const products = [];
  while (next && !seen.has(next)) {
   seen.add(next);
   const response = await fetch(next);
   if (!response.ok) throw new Error('Pairing data unavailable');
   const doc = new DOMParser().parseFromString(await response.text(),'text/html');
   const catalog = doc.querySelector('[data-lr-cart-catalog]');
   if (!catalog) throw new Error('Pairing data missing');
   products.push(...[...catalog.querySelectorAll('article[data-product-id]')].map(element => ({
    id:element.dataset.productId, title:element.dataset.title, colour:element.dataset.colour,
    type:element.dataset.type, tags:element.dataset.tags, url:element.dataset.url,
    image:element.dataset.image, option:element.dataset.optionLabel, defaultVariant:element.dataset.defaultVariant === 'true',
    variants:[...element.querySelectorAll('[data-variant-id]')].map(variant => ({
     id:variant.dataset.variantId, price:Number(variant.dataset.price), title:variant.dataset.title
    }))
   })));
   const href = catalog.querySelector('[data-catalog-next]')?.getAttribute('href');
   if (href) { const page = new URL(href, location.origin); page.searchParams.set('section_id','lievre-cart-pair'); next = page.href; }
   else next = null;
  }
  return products;
 })();
 lievrePairCatalogCache.set(url,{time:Date.now(),promise});
 try { return await promise; }
 catch (error) { lievrePairCatalogCache.delete(url); throw error; }
}
/* Recently viewed pieces stay in this browser tab for the current visit. */
const lievreRecentKey = 'lievre-recent-products-v1';
function lievreRecentProducts() {
 try {
  const entries = JSON.parse(sessionStorage.getItem(lievreRecentKey) || '[]');
  const cutoff = Date.now() - 86400000;
  const seen = new Set();
  return (Array.isArray(entries) ? entries : []).filter(entry => {
   if (!entry || !/^\d+$/.test(String(entry.id)) || !Number.isFinite(entry.time) || entry.time < cutoff || entry.time > Date.now() + 60000 || seen.has(String(entry.id))) return false;
   seen.add(String(entry.id)); return true;
  }).slice(0,20);
 } catch { return []; }
}
class LievreRecentProduct extends HTMLElement {
 connectedCallback() {
  const ids = new Set((this.dataset.variants || '').split(','));
  this.remember = variantId => {
   const id = this.dataset.productId;
   if (!/^\d+$/.test(id || '')) return;
   const previous = lievreRecentProducts();
   if (ids.has(String(variantId))) this.dataset.selectedVariant = String(variantId);
   const selected = String(variantId || this.dataset.selectedVariant || '');
   const entry = {id,time:Date.now()};
   if (ids.has(selected)) entry.variantId = selected;
   else if (previous.find(item => String(item.id) === id)?.variantId) entry.variantId = previous.find(item => String(item.id) === id).variantId;
   try { sessionStorage.setItem(lievreRecentKey,JSON.stringify([entry,...previous.filter(item => String(item.id) !== id)].slice(0,20))); } catch {}
  };
  this.remember();
  this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange,({data}) => {
   if (ids.has(String(data?.variant?.id))) this.remember(data.variant.id);
  });
  this.onPageShow = event => { if (event.persisted) this.remember(); };
  window.addEventListener('pageshow',this.onPageShow);
 }
 disconnectedCallback() { this.unsubscribe?.(); window.removeEventListener('pageshow',this.onPageShow); }
}
if (!customElements.get('lr-recent-product')) customElements.define('lr-recent-product',LievreRecentProduct);

function lievreRankCartPairs(catalog,items,recent,remaining) {
 const normalize = text => (text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim();
 const colourFamilies = value => {
  const families = {blue:/blue|navy|indigo|bleu|turquoise|teal|cyan/,white:/white|ivory|cream|ecru|blanc/,black:/black|noir/,grey:/grey|gray|charcoal|gris/,pink:/pink|rose|fuchsia/,red:/red|burgundy|rouge/,green:/green|olive|vert/,yellow:/yellow|jaune/,brown:/brown|beige|tan|earth/,purple:/purple|violet|lavender/,orange:/orange|coral|salmon/};
  let tags = [];
  try { tags = JSON.parse(value.tags || '[]').filter(tag => tag.startsWith('color-')).map(tag => tag.slice(6)); } catch {}
  const colour = normalize(tags.length ? tags.join(' ') : value.colour);
  return Object.keys(families).filter(key => families[key].test(colour));
 };
 const kind = item => {
  const text = normalize(item.type + ' ' + item.title);
  if (/cap|hat/.test(text)) return 'cap';
  if (/bandana|gavroche/.test(text)) return 'bandana';
  if (/tank|singlet/.test(text)) return 'tank';
  if (/long sleeve|\bls\b/.test(text)) return 'long-sleeve';
  if (/shirt|tee|\bss\b/.test(text)) return 'tee';
  return 'other';
 };
 const existing = new Set(items.map(item => String(item.id)));
 const basketFamilies = new Set(items.flatMap(colourFamilies));
 const basketKinds = new Set(items.map(kind));
 const recentPositions = new Map(recent.map((item,index) => [String(item.id),index]));
 const seen = new Set();
 const ranked = catalog.filter(candidate => {
  const id = String(candidate.id);
  if (existing.has(id) || seen.has(id) || !candidate.variants.length) return false;
  seen.add(id); return true;
 }).map((candidate,index) => {
  const variants = [...candidate.variants].filter(variant => Number.isFinite(variant.price) && variant.price >= 0).sort((a,b) => a.price-b.price);
  const families = colourFamilies(candidate);
  const exact = items.some(item => normalize(item.colour) && normalize(item.colour) === normalize(candidate.colour));
  const sameFamily = families.some(family => basketFamilies.has(family));
  const neutral = families.some(family => ['white','black','grey'].includes(family));
  const history = recent.find(item => String(item.id) === String(candidate.id));
  return {candidate,variants,index,colour:exact ? 0 : sameFamily ? 1 : neutral ? 2 : 3,
   complementary:basketKinds.has(kind(candidate)) ? 1 : 0,
   qualifies:variants.some(variant => variant.price >= remaining),
   recent:recentPositions.has(String(candidate.id)),recency:recentPositions.get(String(candidate.id)),
   preferredVariant:history?.variantId,remaining};
 }).filter(entry => entry.variants.length);
 const viewed = ranked.filter(entry => entry.recent).sort((a,b) => a.recency-b.recency);
 // Keep the customer's most recent interest first; reserve the other places for pairings.
 const chosen = viewed.length ? [{...viewed[0],reason:'Recently viewed'}] : [];
 const matching = ranked.filter(entry => entry.colour < 3 && !chosen.some(other => other.candidate.id === entry.candidate.id))
  .sort((a,b) => a.complementary-b.complementary || a.colour-b.colour || Number(b.qualifies)-Number(a.qualifies) || a.variants[0].price-b.variants[0].price || a.index-b.index);
 for (const entry of matching) {
  chosen.push({...entry,reason:'Pairs well with your order'});
  if (chosen.length === 3) break;
 }
 // If there are no useful colour matches, another viewed piece is more relevant than a random item.
 for (const entry of viewed) {
  if (chosen.length === 3) break;
  if (!chosen.some(other => String(other.candidate.id) === String(entry.candidate.id))) chosen.push({...entry,reason:'Recently viewed'});
 }
 return chosen;
}

class LievreCartPair extends HTMLElement {
 connectedCallback() {
  this.drawer = this.closest('cart-drawer');
  if (!this.drawer) return;
  this.openObserver = new MutationObserver(() => { if (this.drawer.classList.contains('active')) this.load(); });
  this.openObserver.observe(this.drawer,{attributes:true,attributeFilter:['class']});
  if (this.drawer.classList.contains('active')) this.load();
 }
 async load() {
  if (this.loading || this.loaded) return;
  const shipping = this.drawer?.querySelector('lr-free-shipping');
  const threshold = shipping ? lievreShippingThreshold(shipping) : null;
  const remaining = threshold === null ? 0 : Math.max(0,threshold - Number(shipping.dataset.total));
  if (!Number.isFinite(remaining) || remaining <= 0) { this.hidden = true; return; }
  this.loading = true;
  try {
   const catalog = await lievrePairCatalog(this.dataset.url);
   if (!this.isConnected) return;
   const items = [...this.querySelectorAll('[data-pair-cart] [data-product-id]')].map(element => ({...element.dataset,id:element.dataset.productId}));
   const offers = lievreRankCartPairs(catalog,items,lievreRecentProducts(),remaining);
   if (!offers.length) { this.hidden = true; return; }
   const list = this.querySelector('[data-cart-pairs-list]');
   const template = this.querySelector('[data-cart-pair-template]');
   const navigation = this.querySelector('[data-cart-pairs-navigation]');
   list.replaceChildren(); navigation.replaceChildren();
   offers.forEach((offer,index) => {
    const fragment = document.importNode(template.content,true);
    const pair = fragment.querySelector('lr-inline-pair');
    pair.dataset.offer = JSON.stringify(offer);
    pair.dataset.currency = this.dataset.currency; pair.dataset.locale = this.dataset.locale;
    pair.setAttribute('role','group'); pair.setAttribute('aria-label',`${index+1} of ${offers.length}: ${offer.candidate.title}`);
    list.append(fragment);
    const button = document.createElement('button');
    button.type = 'button'; button.dataset.pairSlide = index;
    button.setAttribute('aria-label',`Show recommendation ${index+1} of ${offers.length}: ${offer.candidate.title}`);
    navigation.append(button);
   });
   this.loaded = true; this.hidden = false;
   this.setupCarousel(list,navigation);
  } catch { this.hidden = true; }
  finally { this.loading = false; }
 }
 setupCarousel(list,navigation) {
  this.carouselAbort?.abort();
  this.carouselResize?.disconnect();
  clearTimeout(this.cartSnapTimer);
  this.carouselAbort = new AbortController();
  const options = {signal:this.carouselAbort.signal};
  const buttons = [...navigation.children];
  const slides = [...list.children];
  navigation.hidden = slides.length <= 1;
  const arrows = this.querySelector('[data-cart-pairs-arrows]');
  const previous = arrows.querySelector('[data-pair-previous]');
  const next = arrows.querySelector('[data-pair-next]');
  arrows.hidden = slides.length <= 1;
  const position = this.querySelector('[data-cart-pair-position]');
  let drag, touching = false;
  list.style.removeProperty('height');
  // Rectangles retain fractional pixels on mobile; offsetLeft rounds them away.
  const leftFor = slide => slide.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
  const nearest = () => slides.reduce((best,slide,i) => Math.abs(leftFor(slide)-list.scrollLeft) < Math.abs(leftFor(slides[best])-list.scrollLeft) ? i : best,0);
  const update = () => {
   const index = nearest();
   previous.disabled = index === 0;
   next.disabled = index === slides.length-1;
   buttons.forEach((button,i) => { if (i === index) button.setAttribute('aria-current','true'); else button.removeAttribute('aria-current'); });
   if (this.slideIndex !== index) { position.textContent = `Recommendation ${index+1} of ${slides.length}`; this.slideIndex = index; }
  };
  const settle = () => {
   clearTimeout(this.cartSnapTimer);
   if (touching || drag?.moved || !this.isConnected || !list.clientWidth) return;
   const index = nearest();
   const left = Math.max(0,Math.min(list.scrollWidth-list.clientWidth,leftFor(slides[index])));
   // Correct any residual offset only after native touch scrolling has finished.
   if (Math.abs(list.scrollLeft-left) > 1) list.scrollTo({left,behavior:'auto'});
   update();
  };
  const queueSettle = () => {
   clearTimeout(this.cartSnapTimer);
   this.cartSnapTimer = setTimeout(settle,160);
  };
  const show = index => {
   const target = Math.max(0,Math.min(slides.length-1,index));
   list.scrollTo({left:leftFor(slides[target]),behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'});
   queueSettle();
  };
  previous.addEventListener('click',() => show(nearest()-1),options);
  next.addEventListener('click',() => show(nearest()+1),options);
  navigation.addEventListener('click',event => {
   const button = event.target.closest('[data-pair-slide]');
   if (button) show(Number(button.dataset.pairSlide));
  },options);
  const keys = event => {
   if (event.target !== list && !event.target.matches('[data-pair-slide]')) return;
   const current = Number(event.target.dataset.pairSlide ?? this.slideIndex ?? 0);
   let next;
   if (event.key === 'ArrowRight') next = current+1;
   else if (event.key === 'ArrowLeft') next = current-1;
   else if (event.key === 'Home') next = 0;
   else if (event.key === 'End') next = slides.length-1;
   else return;
   event.preventDefault(); next = Math.max(0,Math.min(slides.length-1,next)); show(next);
   if (event.target !== list) buttons[next].focus({preventScroll:true});
  };
  list.addEventListener('keydown',keys,options); navigation.addEventListener('keydown',keys,options);
  list.addEventListener('scroll',() => { update(); queueSettle(); },{...options,passive:true});
  list.addEventListener('scrollend',settle,options);
  list.addEventListener('touchstart',() => { touching = true; clearTimeout(this.cartSnapTimer); },{...options,passive:true});
  const touchFinished = () => { touching = false; queueSettle(); };
  list.addEventListener('touchend',touchFinished,{...options,passive:true});
  list.addEventListener('touchcancel',touchFinished,{...options,passive:true});
  // Touch stays native; desktop also has previous/next controls.
  list.addEventListener('pointerdown',event => {
   if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select')) return;
   drag = {id:event.pointerId,x:event.clientX,scroll:list.scrollLeft,moved:false};
  },options);
  list.addEventListener('pointermove',event => {
   if (!drag || event.pointerId !== drag.id) return;
   if (!(event.buttons & 1)) { finish(); return; }
   const delta = event.clientX-drag.x;
   if (Math.abs(delta) <= 5 && !drag.moved) return;
   drag.moved = true; list.setPointerCapture(event.pointerId);
   list.classList.add('is-dragging'); list.scrollLeft = drag.scroll-delta;
   event.preventDefault();
  },options);
  const finish = () => {
   if (!drag) return;
   if (drag.moved) this.suppressLinkUntil = Date.now()+250;
   if (list.hasPointerCapture(drag.id)) list.releasePointerCapture(drag.id);
   list.classList.remove('is-dragging'); drag = null;
   queueSettle();
  };
  list.addEventListener('pointerup',finish,options); list.addEventListener('pointercancel',finish,options);
  list.addEventListener('pointerleave',() => { if (!drag?.moved) { drag = null; queueSettle(); } },options);
  list.addEventListener('dragstart',event => event.preventDefault(),options);
  list.addEventListener('click',event => { if (Date.now() < (this.suppressLinkUntil || 0)) { event.preventDefault(); event.stopPropagation(); } },{...options,capture:true});
  this.carouselResize = new ResizeObserver(update);
  this.carouselResize.observe(list);
  slides.forEach(slide => this.carouselResize.observe(slide));
  update();
 }
 render() {
  const {candidate,variants} = this.offer;
  this.variant = null;
  this.querySelector('[data-pair-add]').disabled = true;
  this.querySelectorAll('[data-pair-link]').forEach(link => { link.href = candidate.url; });
  this.querySelector('[data-pair-title]').textContent = candidate.title;
  this.querySelector('[data-pair-colour]').textContent = candidate.colour;
  const image = this.querySelector('[data-pair-image]');
  const imageUrl = new URL(candidate.image,location.origin);
  if (this.closest('lr-cart-pair')) imageUrl.searchParams.set('width','320');
  image.src = imageUrl.href; image.alt = candidate.title + (candidate.colour ? ' in ' + candidate.colour : '');
  this.querySelector('[data-pair-price]').textContent = lievreMoney(variants[0].price,this.dataset.currency,this.dataset.locale);
  const choices = this.querySelector('[data-pair-variants]');
  choices.replaceChildren();
  const oneSize = candidate.defaultVariant || /^(default title|one[ -]?size|os|o[/]s)$/i.test(variants[0].title.trim());
  if (variants.length === 1 && oneSize) {
   choices.hidden = true;
   this.selectVariant(variants[0].id);
  } else {
   choices.hidden = false;
   choices.setAttribute('aria-label',candidate.option + ' for ' + candidate.title);
   const label = document.createElement('span'); label.textContent = candidate.option; choices.append(label);
   variants.forEach(variant => {
    const button = document.createElement('button'); button.type = 'button';
    button.dataset.pairVariant = variant.id; button.textContent = variant.title;
    button.setAttribute('aria-pressed','false'); choices.append(button);
   });
   this.querySelector('[data-pair-add]').textContent = 'Choose ' + candidate.option.toLowerCase();
  }
  if (this.offer.preferredVariant) this.selectVariant(String(this.offer.preferredVariant));
 }
 selectVariant(id) {
  const variant = this.offer.variants.find(option => option.id === id);
  if (!variant) return;
  this.variant = variant;
  this.querySelectorAll('[data-pair-variant]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.pairVariant === id)));
  this.querySelector('[data-pair-price]').textContent = lievreMoney(variant.price,this.dataset.currency,this.dataset.locale);
  const add = this.querySelector('[data-pair-add]'); add.disabled = false; add.textContent = 'Add';
 }
 async add() {
  const group = this.closest('lr-cart-pair') || this.closest('lr-product-pairs');
  if (this.busy || group?.hasAttribute('data-adding') || !this.variant) return;
  this.busy = true; group?.setAttribute('data-adding','');
  const add = this.querySelector('[data-pair-add]');
  const error = this.querySelector('[data-pair-error]');
  error.hidden = true;
  add.disabled = true; add.textContent = 'Adding';
  this.querySelectorAll('[data-pair-variant]').forEach(button => { button.disabled = true; });
  try {
   const response = await fetch(window.routes.cart_add_url,{
    method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json','X-Requested-With':'XMLHttpRequest'},
    body:JSON.stringify({items:[{id:Number(this.variant.id),quantity:1}],sections:this.drawer.getSectionsToRender().map(section => section.id),sections_url:location.pathname})
   });
   const result = await response.json();
   if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'This piece could not be added. Please try again.');
   this.drawer.classList.remove('is-empty');
   this.drawer.setActiveElement(document.querySelector('#cart-icon-bubble'));
   publish(PUB_SUB_EVENTS.cartUpdate,{source:'lr-cart-pair',productVariantId:this.variant.id,cartData:result});
   this.drawer.renderContents(result);
  } catch (failure) {
   if (this.isConnected) { error.textContent = failure.message; error.hidden = false; }
  } finally {
   this.busy = false; group?.removeAttribute('data-adding');
   if (this.isConnected) {
    add.disabled = false; add.textContent = 'Add';
    this.querySelectorAll('[data-pair-variant]').forEach(button => { button.disabled = false; });
   }
  }
 }
 disconnectedCallback() { this.openObserver?.disconnect(); this.carouselAbort?.abort(); this.carouselResize?.disconnect(); clearTimeout(this.cartSnapTimer); this.removeEventListener('click',this.handleClick); }
}
if (!customElements.get('lr-cart-pair')) customElements.define('lr-cart-pair',LievreCartPair);

/* Product page pairs reuse the proven size selection and cart addition controls. */
class LievreInlinePair extends LievreCartPair {
 connectedCallback() {
  this.drawer = document.querySelector('cart-drawer');
  try { this.offer = JSON.parse(this.dataset.offer); } catch { this.hidden = true; return; }
  this.handleClick = event => {
   const option = event.target.closest('[data-pair-variant]');
   if (option) this.selectVariant(option.dataset.pairVariant);
   if (event.target.closest('[data-pair-add]')) this.add();
  };
  this.addEventListener('click',this.handleClick);
  this.render();
 }
}
if (!customElements.get('lr-inline-pair')) customElements.define('lr-inline-pair',LievreInlinePair);
class LievreProductPairs extends HTMLElement {
 async connectedCallback() {
  if (this.started) return;
  this.started = true;
  try {
   const catalog = await lievrePairCatalog(this.dataset.url);
   if (!this.isConnected) return;
   const normalize = text => (text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim();
   const families = text => {
    const map = {blue:/blue|navy|indigo|bleu|turquoise|teal|cyan/,white:/white|ivory|cream|ecru|blanc/,black:/black|noir/,grey:/grey|gray|charcoal|gris/,pink:/pink|rose|fuchsia/,red:/red|burgundy|rouge/,green:/green|olive|vert/,yellow:/yellow|jaune/,brown:/brown|beige|tan|earth/,purple:/purple|violet|lavender/,orange:/orange|coral|salmon/};
    return Object.keys(map).filter(key => map[key].test(normalize(text)));
   };
   const kind = item => {
    const text = normalize(item.type + ' ' + item.title);
    if (/cap|hat/.test(text)) return 'cap';
    if (/bandana|gavroche/.test(text)) return 'bandana';
    if (/tank|singlet/.test(text)) return 'tank';
    if (/long sleeve|\bls\b/.test(text)) return 'long-sleeve';
    if (/shirt|tee|\bss\b/.test(text)) return 'tee';
    return 'other';
   };
   const colour = normalize(this.dataset.colour);
   const currentFamilies = families(colour);
   const currentKind = kind(this.dataset);
   const ranked = catalog.filter(candidate => candidate.id !== this.dataset.productId && candidate.variants.length)
    .map((candidate,index) => ({
     candidate,index,kind:kind(candidate),
     colour:colour && colour === normalize(candidate.colour) ? 0 : currentFamilies.some(family => families(candidate.colour).includes(family)) ? 1 : 2,
     complementary:kind(candidate) === currentKind ? 1 : 0,
     variants:[...candidate.variants].sort((a,b) => a.price-b.price)
    })).sort((a,b) => a.colour-b.colour || a.complementary-b.complementary || a.index-b.index);
   const chosen = [];
   const seen = new Set();
   for (const entry of ranked) {
    if (seen.has(entry.candidate.id)) continue;
    if (chosen.length && entry.kind === chosen[0].kind && ranked.some(other => !seen.has(other.candidate.id) && other.kind !== chosen[0].kind && other.colour <= entry.colour)) continue;
    chosen.push(entry); seen.add(entry.candidate.id);
    if (chosen.length === 3) break;
   }
   const list = this.querySelector('[data-product-pairs-list]');
   list.replaceChildren();
   chosen.forEach(entry => {
    const fragment = document.importNode(this.querySelector('[data-product-pair-template]').content,true);
    const pair = fragment.querySelector('lr-inline-pair');
    pair.dataset.offer = JSON.stringify({candidate:entry.candidate,variants:entry.variants});
    pair.dataset.currency = this.dataset.currency; pair.dataset.locale = this.dataset.locale;
    list.append(fragment);
   });
   this.hidden = chosen.length === 0;
   this.carouselAbort = new AbortController();
   const options = {signal:this.carouselAbort.signal};
   const track=this.querySelector('.lr-pairs-progress');
   const fill=track.querySelector('span');
   const update = () => {
    const count=list.children.length;
    const max=list.scrollWidth-list.clientWidth;
    const fraction=max>0?Math.max(0,Math.min(1,list.scrollLeft/max)):0;
    track.hidden=count<=1;
    fill.style.width=100/Math.max(1,count)+'%';
    fill.style.transform='translateX('+fraction*Math.max(0,count-1)*100+'%)';
   };
   list.addEventListener('scroll',update,{...options,passive:true});
   list.addEventListener('keydown',event=>{
    if(event.target!==list||!['ArrowLeft','ArrowRight'].includes(event.key))return;
    event.preventDefault();
    list.scrollBy({left:(event.key==='ArrowRight'?1:-1)*(list.clientWidth+16),behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'});
   },options);
   update();
   const info = this.closest('.product__info-container');
   if(info){
    this.infoObserver=new ResizeObserver(()=>{
     const value=info.offsetHeight+'px';
     if(info.style.getPropertyValue('--lr-info-height')!==value)info.style.setProperty('--lr-info-height',value);
    });
    this.infoObserver.observe(info);
   }
  } catch { this.hidden = true; }
 }
}
LievreProductPairs.prototype.disconnectedCallback = function(){this.carouselAbort?.abort();this.infoObserver?.disconnect();};
if (!customElements.get('lr-product-pairs')) customElements.define('lr-product-pairs',LievreProductPairs);

/* Homepage boutique: preserve capsule order while combining collection, colour and headwear filters. */
class LievreHomeCatalog extends HTMLElement {
 async connectedCallback() {
  this.abort = new AbortController();
  this.grid = this.querySelector('.product-grid');
  this.controls = this.querySelector('.lr-home-filter-bar');
  this.menu = this.querySelector('[data-home-colour-menu]');
  this.collectionMenu = this.querySelector('[data-home-collection-menu]');
  try {
   await this.loadRemainingPages();
   if (!this.isConnected) return;
   this.products = [...this.querySelectorAll('[data-home-product]')].map(element => {
    const product = LievreBoutique.prototype.productMetadata.call(this,element);
    try { product.collections = JSON.parse(element.dataset.homeCollections); } catch { product.collections = []; }
    product.headwear = product.collections.includes(this.dataset.headwear);
    let tags = [];
    try { tags = JSON.parse(element.dataset.productTags).map(tag => tag.trim().toLowerCase()); } catch {}
    product.tops = tags.includes('tops');
    return product;
   });
   this.querySelectorAll('[data-home-collection]').forEach(button => {
    button.hidden = button.dataset.homeCollection !== 'all' && !this.products.some(product => product.collections.includes(button.dataset.homeCollection));
   });
   this.querySelectorAll('.lr-home-collection-list [data-home-collection]').forEach(button => {
    this.querySelector('[data-home-collection-options]').append(button.cloneNode(true));
   });
   this.menu.addEventListener('toggle',this.onColourToggle = () => { if (this.menu.open) this.collectionMenu.open = false; });
   this.collectionMenu.addEventListener('toggle',this.onCollectionToggle = () => { if (this.collectionMenu.open) this.menu.open = false; });
   const colours = [...new Set(this.products.flatMap(product => product.colours))].sort((a,b) => a.localeCompare(b));
   colours.forEach(colour => {
    const button = document.createElement('button');
    button.type = 'button'; button.dataset.homeColour = colour; button.textContent = colour; button.setAttribute('aria-pressed','false');
    this.querySelector('[data-home-colour-options]').append(button);
   });
   this.onClick = event => {
    const categoryLink = event.target.closest('a[data-home-headwear],a[data-home-tops]');
    if (categoryLink) {
     if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
     event.preventDefault();
    }
    const collection = event.target.closest('[data-home-collection]');
    const colour = event.target.closest('[data-home-colour]');
    if (collection) { this.collection = collection.dataset.homeCollection; this.collectionMenu.open = false; if (collection.closest('[data-home-collection-menu]')) this.collectionMenu.querySelector('summary').focus({preventScroll:true}); }
    if (colour) { this.colour = colour.dataset.homeColour; this.menu.open = false; this.menu.querySelector('summary').focus({preventScroll:true}); }
    if (event.target.closest('[data-home-headwear]')) { this.headwear = !this.headwear; this.tops = false; }
    if (event.target.closest('[data-home-tops]')) { this.tops = !this.tops; this.headwear = false; }
    if (event.target.closest('[data-home-reset]')) { this.collection = 'all'; this.colour = 'all'; this.headwear = false; this.tops = false; this.menu.open = false; this.collectionMenu.open = false; }
    if (collection || colour || event.target.closest('[data-home-headwear],[data-home-tops],[data-home-reset]')) this.applyFilters(true);
   };
   this.onOutside = event => { if (!this.menu.contains(event.target)) this.menu.open = false; if (!this.collectionMenu.contains(event.target)) this.collectionMenu.open = false; };
   this.onEscape = event => { if (event.key === ' ' && event.target.matches('a[data-home-headwear],a[data-home-tops]')) { event.preventDefault(); event.target.click(); return; } if (event.key !== 'Escape') return; for (const menu of [this.menu,this.collectionMenu]) if (menu.open) { menu.open = false; menu.querySelector('summary').focus({preventScroll:true}); } };
   this.onHistory = () => { this.readFilters(); this.applyFilters(false); };
   this.controls.addEventListener('click',this.onClick);
   document.addEventListener('click',this.onOutside);
   this.addEventListener('keydown',this.onEscape);
   window.addEventListener('popstate',this.onHistory);
   this.readFilters(); this.controls.hidden = false; this.applyFilters(false);
   this.querySelector('[data-home-next]')?.setAttribute('hidden','');
  } catch(error) {
   if (error.name !== 'AbortError') this.querySelector('[data-home-status]').textContent = 'All pieces shown. Filters are temporarily unavailable.';
  }
 }
 async loadRemainingPages() {
  let next = this.querySelector('[data-home-next]')?.getAttribute('href');
  const pages = new Set();
  const ids = new Set([...this.querySelectorAll('[data-home-product]')].map(item => item.dataset.homeProduct));
  while (next && !pages.has(next)) {
   pages.add(next);
   const url = new URL(next,location.href); url.searchParams.set('section_id',this.dataset.sectionId);
   const response = await fetch(url,{signal:this.abort.signal});
   if (!response.ok) throw new Error('Catalogue unavailable');
   const doc = new DOMParser().parseFromString(await response.text(),'text/html');
   const catalog = doc.querySelector('lievre-home-catalog');
   if (!catalog) throw new Error('Catalogue missing');
   catalog.querySelectorAll('[data-home-product]').forEach(item => {
    if (!ids.has(item.dataset.homeProduct)) { ids.add(item.dataset.homeProduct); this.grid.append(document.importNode(item,true)); }
   });
   next = catalog.querySelector('[data-home-next]')?.getAttribute('href');
  }
 }
 readFilters() {
  const params = new URL(location.href).searchParams;
  this.collection = params.get('lr_collection') || 'all';
  if (![...this.querySelectorAll('[data-home-collection]')].some(button => !button.hidden && button.dataset.homeCollection === this.collection)) this.collection = 'all';
  this.colour = params.get('lr_colour') || 'all';
  if (![...this.querySelectorAll('[data-home-colour]')].some(button => button.dataset.homeColour === this.colour)) this.colour = 'all';
  this.tops = params.get('lr_tops') === '1';
  this.headwear = !this.tops && params.get('lr_headwear') === '1';
 }
 applyFilters(updateUrl) {
  let count = 0;
  this.products.forEach(product => {
   const visible = (this.collection === 'all' || product.collections.includes(this.collection)) &&
    (this.colour === 'all' || product.colours.includes(this.colour)) && (!this.headwear || product.headwear) && (!this.tops || product.tops);
   product.element.hidden = !visible;
   if (visible) count++;
  });
  this.querySelectorAll('[data-home-collection]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.homeCollection === this.collection)));
  this.querySelectorAll('[data-home-colour]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.homeColour === this.colour)));
  const activeCollection = [...this.querySelectorAll('.lr-home-collection-list [data-home-collection]')].find(button => button.dataset.homeCollection === this.collection);
  this.querySelector('[data-home-collection-label]').textContent = this.collection === 'all' ? 'Collection' : activeCollection?.textContent.trim() || 'Collection';
  this.collectionMenu.querySelector('summary').setAttribute('aria-label','Collection filter: ' + (this.collection === 'all' ? 'All' : activeCollection?.textContent.trim()));
  this.querySelector('[data-home-headwear]').setAttribute('aria-pressed',String(this.headwear));
  this.querySelector('[data-home-tops]').setAttribute('aria-pressed',String(this.tops));
  this.querySelector('[data-home-colour-label]').textContent = this.colour === 'all' ? 'Colour' : this.colour;
  this.menu.querySelector('summary').setAttribute('aria-label','Colour filter: ' + (this.colour === 'all' ? 'All' : this.colour));
  this.querySelector('[data-home-reset]').hidden = this.collection === 'all' && this.colour === 'all' && !this.headwear && !this.tops;
  this.querySelector('[data-home-empty]').hidden = count > 0;
  this.querySelector('[data-home-status]').textContent = count + (count === 1 ? ' piece shown' : ' pieces shown');
  if (updateUrl) {
   const url = new URL(location.href);
   this.collection === 'all' ? url.searchParams.delete('lr_collection') : url.searchParams.set('lr_collection',this.collection);
   this.colour === 'all' ? url.searchParams.delete('lr_colour') : url.searchParams.set('lr_colour',this.colour);
   this.headwear ? url.searchParams.set('lr_headwear','1') : url.searchParams.delete('lr_headwear');
   this.tops ? url.searchParams.set('lr_tops','1') : url.searchParams.delete('lr_tops');
   history.replaceState(history.state,'',url);
  }
 }
 disconnectedCallback() {
  this.abort?.abort(); this.controls?.removeEventListener('click',this.onClick);
  this.menu?.removeEventListener('toggle',this.onColourToggle); this.collectionMenu?.removeEventListener('toggle',this.onCollectionToggle);
  document.removeEventListener('click',this.onOutside); this.removeEventListener('keydown',this.onEscape);
  window.removeEventListener('popstate',this.onHistory);
 }
}
if (!customElements.get('lievre-home-catalog')) customElements.define('lievre-home-catalog',LievreHomeCatalog);

/* Shopify customer form registers mailing-list subscribers, tagged by item and variant. */
class LievreRestock extends HTMLElement {
 connectedCallback(){
  this.product=this.closest('product-info');
  if(!this.product){this.hidden=true;return;}
  this.controller=new AbortController();
  this.trigger=this.querySelector('.lr-restock-trigger');
  this.panel=this.querySelector('.lr-restock-panel');
  const options={signal:this.controller.signal};
  this.trigger.addEventListener('click',()=>this.open(),options);
  this.querySelector('.lr-restock-close').addEventListener('click',()=>this.panel.close(),options);
  this.panel.addEventListener('close',()=>this.trigger.setAttribute('aria-expanded','false'),options);
  this.panel.addEventListener('click',event=>{
   if(event.target!==this.panel)return;
   const rect=this.panel.getBoundingClientRect();
   if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)this.panel.close();
  },options);
  this.unsubscribe=subscribe(PUB_SUB_EVENTS.variantChange,({data})=>{
   if(String(data.sectionId)!==String(this.product.dataset.section))return;
   this.update(data.variant);
  });
  const data=this.product.querySelector('variant-selects [data-selected-variant]');
  let variant;
  try{variant=JSON.parse(data?.textContent||'null');}catch{}
  if(variant)this.update(variant);
  else this.apply(this.hasAttribute('data-sold-out'));
  if(this.querySelector('[data-restock-success]'))this.open();
 }
 update(variant){
  const sold=!!variant && !variant.available;
  if(variant){
   this.dataset.variantId=variant.id;
   this.querySelector('[data-restock-tags]').value='newsletter,restock-'+this.dataset.productHandle+',restock-variant-'+variant.id;
  }
  this.apply(sold);
 }
 apply(sold){
  this.toggleAttribute('data-sold-out',sold);
  this.hidden=!sold;
  const native=this.product.querySelector('product-form button[name="add"]');
  native?.classList.toggle('lr-sold-out-native',sold);
  if(!sold){if(this.panel.open)this.panel.close();this.trigger.setAttribute('aria-expanded','false');}
  this.product.querySelector('lr-product-purchase')?.sync();
 }
 open(){
  if(!this.panel.open)this.panel.showModal();
  this.trigger.setAttribute('aria-expanded','true');
  this.querySelector('input[type="email"]')?.focus({preventScroll:true});
 }
 disconnectedCallback(){this.controller?.abort();this.unsubscribe?.();}
}
if(!customElements.get('lr-restock'))customElements.define('lr-restock',LievreRestock);

/* Size estimates use the item's garment chart. Measurements stay in page memory only. */
function lievreReadSizeChart(table, cells) {
 const headings = [...table.rows[0].cells].map(cell => cell.textContent.trim().toLowerCase());
 const chestIndex = headings.findIndex(text => /chest|pit.to.pit|bust/.test(text));
 const shoulderIndex = headings.findIndex(text => /shoulder/.test(text));
 const lengthIndex = headings.findIndex(text => /length/.test(text) && !/sleeve/.test(text));
 if ((chestIndex < 1 && shoulderIndex < 1) || lengthIndex < 1) return [];
 const measurements = new Map(cells.map(({cell,cm}) => [cell,cm]));
 const dimension = chestIndex > 0 ? 'chest' : 'shoulder';
 const widthIndex = chestIndex > 0 ? chestIndex : shoulderIndex;
 return [...table.querySelectorAll('tbody tr')].map(row => ({
  size:row.cells[0]?.textContent.trim(),
  [dimension]:measurements.get(row.cells[widthIndex]),
  length:measurements.get(row.cells[lengthIndex])
 })).filter(row => /^(XXS|XS|S|M|L|XL|XXL|XXXL|[2-5]XL)$/i.test(row.size) && row[dimension] > 25 && row.length > 30)
  .map(row => dimension === 'chest' ? {...row,chest:row.chest > 75 ? row.chest / 2 : row.chest} : row)
  .sort((a,b) => a[dimension] - b[dimension]);
}
function lievreRecommendSize(chart, height, weight, fit) {
 height = Math.round(height * 10) / 10; weight = Math.round(weight * 10) / 10;
 if (chart.length < 2 || !Number.isFinite(height) || !Number.isFinite(weight) || height < 130 || height > 220 || weight < 30 || weight > 200 || !['fitted','regular','loose'].includes(fit)) return null;
 // Height and weight give an estimate only, never a measured chest or shoulder.
 // Shoulder-only charts retain their actual dimensions; no chest data is fabricated.
 const shoulderChart = chart.every(row => Number.isFinite(row.shoulder));
 const dimension = shoulderChart ? 'shoulder' : 'chest';
 const width = shoulderChart
  ? height * .24 + (weight - 75) * .075 + {fitted:-1.5,regular:0,loose:1.5}[fit]
  : (154 * Math.sqrt(weight / height) + {fitted:2,regular:6,loose:10}[fit]) / 2;
 const mid = chart[Math.floor(chart.length / 2)];
 const length = height * (shoulderChart || mid.length >= 70 ? .38 : .36);
 const step = shoulderChart ? 1.5 : 2.5;
 if (width > chart[chart.length - 1][dimension] + step || length > Math.max(...chart.map(row => row.length)) + 8) return {...chart[chart.length - 1],outside:'large'};
 if (width < chart[0][dimension] - (shoulderChart ? 3 : 5)) return {...chart[0],outside:'small'};
 return chart.reduce((best,row) => {
  const score = item => Math.abs(item[dimension] - width) / step + Math.abs(item.length - length) / (shoulderChart ? 4 : 8);
  return score(row) < score(best) ? row : best;
 });
}
function lievreParseHeight(value, units) {
 const text = String(value).trim().replace(/[′’]/g,"'").replace(/[″“”]/g,'"');
 if (units === 'metric') return /^\d+(?:[.,]\d+)?$/.test(text) ? Number(text.replace(',','.')) : NaN;
 const match = text.match(/^(\d)\s*(?:'|ft|feet)\s*(\d{1,2}(?:\.\d+)?)?\s*(?:"|in|inches)?$/i)
  || text.match(/^(\d)\s+(\d{1,2}(?:\.\d+)?)$/);
 if (!match) return NaN;
 const feet = Number(match[1]), inches = Number(match[2] || 0);
 return feet >= 4 && feet <= 7 && inches >= 0 && inches < 12 ? (feet * 12 + inches) * 2.54 : NaN;
}
function lievreFormatHeight(cm) {
 const total = Math.round(cm / 2.54);
 return Math.floor(total / 12) + "'" + (total % 12) + '"';
}
class LievreSizeFinder extends HTMLElement {
 connectedCallback() {
  if (this.querySelector('form') || !this.chart?.length) return;
  this.units = 'metric';
  const group = 'lr-fit-' + (++LievreSizeFinder.count);
  this.innerHTML = `<form autocomplete="off">
   <div class="lr-finder-heading"><div class="lr-size-units" role="group" aria-label="Your measurement units">
    <button type="button" data-finder-unit="metric" aria-pressed="true">CM / KG</button><button type="button" data-finder-unit="imperial" aria-pressed="false">FT / LB</button>
   </div></div>
   <div class="lr-finder-fields">
    <label><span data-height-label>Height (cm)</span><input type="text" inputmode="decimal" required aria-label="Height (cm)" placeholder="e.g. 180" data-finder-height></label>
    <label><span data-weight-label>Weight (kg)</span><input type="number" inputmode="decimal" step="any" min="30" max="200" required aria-label="Weight (kg)" placeholder="e.g. 70" data-finder-weight></label>
   </div>
   <fieldset><legend>Preferred fit</legend><div class="lr-finder-fits">
    <label><input type="radio" name="${group}" value="fitted"><span>Fitted</span></label>
    <label><input type="radio" name="${group}" value="regular" checked><span>Regular</span></label>
    <label><input type="radio" name="${group}" value="loose"><span>Loose</span></label>
   </div></fieldset>
   <p class="lr-finder-privacy">By selecting Find my size, you agree to use your measurements for this recommendation. Never saved or shared.</p>
   <button type="submit" class="lr-finder-submit">Find my size</button>
  </form><div class="lr-finder-result" role="status" aria-live="polite" tabindex="-1" hidden></div>`;
  this.controller = new AbortController();
  const options = {signal:this.controller.signal};
  this.form = this.querySelector('form');
  this.result = this.querySelector('.lr-finder-result');
  this.form.addEventListener('submit',event => {
   event.preventDefault();
   const height = lievreParseHeight(this.querySelector('[data-finder-height]').value,this.units);
   const weight = this.querySelector('[data-finder-weight]').valueAsNumber * (this.units === 'metric' ? 1 : .45359237);
   const heightInput = this.querySelector('[data-finder-height]');
   if (!Number.isFinite(height) || height < 130 || height > 220) {
    heightInput.setCustomValidity(this.units === 'metric' ? 'Enter a height between 130 and 220 cm.' : `Enter your height in feet and inches, e.g. 5'7".`);
    heightInput.reportValidity();
    return;
   }
   heightInput.setCustomValidity('');
   const recommendation = lievreRecommendSize(this.chart,height,weight,this.querySelector('input[type=radio]:checked').value);
   this.result.replaceChildren();
   const title = document.createElement('p');
   const subtitle = document.createElement('p');
   const note = document.createElement('p');
   title.textContent = recommendation ? (recommendation.outside ? 'You could try size ' : 'Size ') + recommendation.size : 'Check your measurements';
   subtitle.textContent = recommendation?.outside
    ? 'Your measurements fall outside our estimated range. ' + recommendation.size + ' is our ' + (recommendation.outside === 'large' ? 'largest' : 'smallest') + ' size; please check the chart before choosing.'
    : recommendation ? 'Recommended for your measurements.' : 'Please enter a valid height and weight.';
   note.textContent = 'An estimate based on this item’s size chart.';
   const retry = document.createElement('button');
   retry.type = 'button'; retry.className = 'lr-finder-retry'; retry.textContent = 'Change my measurements';
   retry.addEventListener('click',() => {
    this.form.hidden = false;
    this.result.hidden = true;
    this.querySelector('[data-finder-height]').focus();
   },options);
   title.className = 'lr-finder-result-size';
   this.result.append(title,subtitle);
   if (recommendation && !recommendation.outside) this.result.append(note);
   this.result.append(retry);
   this.form.hidden = true;
   this.result.hidden = false;
   this.result.focus({preventScroll:true});
  },options);
  this.form.addEventListener('input',() => { this.querySelector('[data-finder-height]').setCustomValidity(''); this.result.hidden = true; },options);
  this.form.addEventListener('click',event => {
   const button = event.target.closest('[data-finder-unit]');
   if (!button || button.dataset.finderUnit === this.units) return;
   const imperial = button.dataset.finderUnit === 'imperial';
   const height = this.querySelector('[data-finder-height]');
   const weight = this.querySelector('[data-finder-weight]');
   const cm = lievreParseHeight(height.value,this.units);
   if (height.value && Number.isFinite(cm)) height.value = imperial ? lievreFormatHeight(cm) : String(Math.round(cm * 10) / 10);
   height.inputMode = imperial ? 'text' : 'decimal';
   height.setCustomValidity('');
   if (weight.value && Number.isFinite(weight.valueAsNumber)) weight.value = (weight.valueAsNumber * (imperial ? 1 / .45359237 : .45359237)).toFixed(2);
   weight.min = imperial ? '66.13' : '30'; weight.max = imperial ? '440.93' : '200';
   const heightLabel = 'Height (' + (imperial ? 'ft / in' : 'cm') + ')';
   const weightLabel = 'Weight (' + (imperial ? 'lb' : 'kg') + ')';
   this.querySelector('[data-height-label]').textContent = heightLabel;
   this.querySelector('[data-weight-label]').textContent = weightLabel;
   height.setAttribute('aria-label',heightLabel); weight.setAttribute('aria-label',weightLabel);
   height.placeholder = imperial ? `e.g. 5'7"` : 'e.g. 180'; weight.placeholder = imperial ? 'e.g. 154' : 'e.g. 70';
   this.units = button.dataset.finderUnit;
   this.querySelectorAll('[data-finder-unit]').forEach(control => control.setAttribute('aria-pressed',String(control === button)));
   this.result.hidden = true;
  },options);
 }
 disconnectedCallback() { this.controller?.abort(); this.replaceChildren(); }
}
LievreSizeFinder.count = 0;
if (!customElements.get('lr-size-finder')) customElements.define('lr-size-finder',LievreSizeFinder);


/* Size assistance shares the product's live variant and purchase state. */
class LievreSizeDrawer extends HTMLElement {
 connectedCallback() {
  this.product = this.closest('product-info');
  this.dialog = this.querySelector('dialog');
  this.trigger = this.product.querySelector('[data-size-drawer-open]');
  this.options = this.querySelector('[data-size-drawer-options]');
  this.purchase = this.querySelector('[data-size-drawer-add]');
  this.controller = new AbortController();
  const events = {signal:this.controller.signal};
  this.trigger?.addEventListener('click',() => this.open(),events);
  this.querySelector('[data-size-drawer-close]').addEventListener('click',() => this.dialog.close(),events);
  this.dialog.addEventListener('click',event => {
   if (event.target !== this.dialog) return;
   const rect = this.dialog.getBoundingClientRect();
   if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
  },events);
  this.dialog.addEventListener('close',() => {
   this.trigger?.setAttribute('aria-expanded','false');
   if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden');
   this.ownsScrollLock = false;
  },events);
  this.purchase.addEventListener('click',() => {
   const restock = this.product.querySelector('lr-restock[data-sold-out]');
   const native = this.product.querySelector('product-form button[name="add"]');
   if (restock) { this.dialog.close(); restock.open(); }
   else if (native && !native.disabled && native.getAttribute('aria-disabled') !== 'true') {
    this.dialog.close();
    native.click();
   }
  },events);
  this.product.addEventListener('change',event => {
   if (event.target.closest('variant-selects')) { this.renderOptions(); this.syncPurchase(); }
  },events);
  this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange,({data}) => {
   if (String(data.sectionId) !== String(this.product.dataset.section)) return;
   this.renderOptions(); this.syncPurchase();
  });
  this.observer = new MutationObserver(() => {
   if (this.frame) return;
   this.frame = requestAnimationFrame(() => {
    this.frame = null;
    if (this.dialog.open) this.syncPurchase();
   });
  });
  this.observer.observe(this.product,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-disabled','data-sold-out']});
  this.mount(this.product.querySelector('lr-product-description'));
 }
 mount(description) {
  if (this.mounted || !description?.classList.contains('lr-description-ready')) return;
  const guide = [...description.querySelectorAll('details')].find(item => /size\s*(guide|chart)/i.test(item.querySelector('summary')?.textContent || ''));
  if (!guide) return;
  const body = this.querySelector('[data-size-drawer-body]');
  [...guide.children].filter(child => child.tagName !== 'SUMMARY').forEach(child => body.append(child));
  if (!body.children.length) return;
  guide.remove();
  this.mounted = true;
  this.trigger.hidden = false;
  this.renderOptions();
  this.syncPurchase();
 }
 open() {
  if (!this.mounted || this.dialog.open) return;
  this.renderOptions(); this.syncPurchase();
  this.ownsScrollLock = !document.body.classList.contains('overflow-hidden');
  document.body.classList.add('overflow-hidden');
  this.dialog.showModal();
  this.trigger.setAttribute('aria-expanded','true');
 }
 renderOptions() {
  const picker = this.product.querySelector('variant-selects');
  const controls = picker ? [...picker.querySelectorAll('fieldset,select')] : [];
  const focused = this.options.contains(document.activeElement) ? document.activeElement : null;
  const focusChoice = focused ? {group:focused.dataset.drawerGroup,value:focused.dataset.drawerValue} : null;
  this.options.replaceChildren();
  controls.forEach((source,index) => {
   const field = document.createElement('div');
   field.className = 'lr-size-drawer-option-row'; field.setAttribute('role','group');
   const legend = document.createElement('span');
   legend.className = 'lr-size-drawer-option-name';
   legend.textContent = source.tagName === 'SELECT'
    ? source.closest('.product-form__input')?.querySelector('label')?.textContent.trim() || 'Size'
    : source.querySelector('legend')?.textContent.trim().replace(/:\s*.*$/,'') || 'Size';
   field.setAttribute('aria-label',legend.textContent);
   field.append(legend);
   const values = document.createElement('div');
   values.className = 'lr-size-drawer-values';
   const inputs = source.tagName === 'SELECT' ? [...source.options] : [...source.querySelectorAll('input[type=radio]')];
   inputs.forEach(input => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = input.value;
    button.dataset.drawerGroup = String(index); button.dataset.drawerValue = input.value;
    const selected = source.tagName === 'SELECT' ? input.selected : input.checked;
    const unavailable = input.disabled || input.classList.contains('disabled');
    button.setAttribute('aria-pressed',String(selected));
    button.classList.toggle('is-sold-out',unavailable);
    if (unavailable) button.setAttribute('aria-label',input.value + ' — sold out');
    // Sold-out sizes remain selectable so restock notifications still work.
    button.addEventListener('click',() => {
     const current = this.product.querySelector('variant-selects')?.querySelectorAll('fieldset,select')[index];
     if (!current) return;
     if (current.tagName === 'SELECT') {
      current.value = input.value;
      current.dispatchEvent(new Event('change',{bubbles:true}));
     } else {
      const radio = [...current.querySelectorAll('input[type=radio]')].find(option => option.value === input.value);
      radio?.click();
     }
     values.querySelectorAll('button').forEach(option => option.setAttribute('aria-pressed',String(option === button)));
     this.purchase.disabled = true;
    },{signal:this.controller.signal});
    values.append(button);
   });
   field.append(values); this.options.append(field);
  });
  if (focusChoice) [...this.options.querySelectorAll('button')].find(button => button.dataset.drawerGroup === focusChoice.group && button.dataset.drawerValue === focusChoice.value)?.focus({preventScroll:true});
 }
 syncPurchase() {
  const selected = [...this.product.querySelectorAll('variant-selects input:checked,variant-selects select')].map(input => input.value.toLowerCase());
  this.querySelectorAll('.lr-size-table tbody tr').forEach(row => row.classList.toggle('lr-chart-selected',selected.includes(row.cells[0]?.textContent.trim().toLowerCase())));
  const restock = !!this.product.querySelector('lr-restock[data-sold-out]');
  const native = this.product.querySelector('product-form button[name="add"]');
  const busy = !restock && (!native || native.disabled || native.getAttribute('aria-disabled') === 'true');
  const label = restock ? 'SOLD OUT - Notify me when available' : native?.querySelector('span')?.textContent.trim() || 'Add to bag';
  if (this.purchase.textContent !== label) this.purchase.textContent = label;
  if (this.purchase.disabled !== busy) this.purchase.disabled = busy;
  this.purchase.classList.toggle('is-restock',restock);
  const price = this.product.querySelector('[data-lr-heading-price]');
  const output = this.querySelector('[data-size-drawer-price]');
  if (price && output.textContent !== price.textContent) output.textContent = price.textContent;
 }
 disconnectedCallback() {
  this.controller?.abort(); this.observer?.disconnect(); this.unsubscribe?.();
  if (this.frame) cancelAnimationFrame(this.frame);
  if (this.dialog?.open) this.dialog.close();
  if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden');
 }
}
if (!customElements.get('lr-size-drawer')) customElements.define('lr-size-drawer',LievreSizeDrawer);


/* A schematic explains flat garment measurements without adding fictitious dimensions. */
function lievreMeasurementHelp(table, product) {
 const headings = [...table.querySelectorAll('thead th')];
 const find = pattern => headings.find(cell => pattern.test(cell.textContent));
 const chest = find(/chest|pit.to.pit|bust|width/i);
 const length = find(/length/i);
 const shoulder = find(/shoulder/i);
 const sleeve = find(/sleeve/i);
 const title = (product?.querySelector('h1')?.textContent || '').toLowerCase().replace(/[‐‑–—]/g,'-');
 if (!length || !/long[\s-]*sleeve|short[\s-]*sleeve|t[\s-]*shirt|\btee\b|singlet|tank/.test(title)) return null;
 const tank = /singlet|tank/.test(title);
 const longSleeve = /long[\s-]*sleeve|\bls\b/.test(title);
 const measurements = [];
 const add = (cell, name, definition, line, x, y) => {
  if (!cell) return;
  const letter = String.fromCharCode(65 + measurements.length);
  cell.textContent = letter + ' · ' + name;
  measurements.push({letter,name,definition,line,x,y});
 };
 add(chest,'Chest width','Armpit to armpit, measured flat.','M96 102H184M96 98V106M184 98V106',140,94);
 add(length,'Body length','Shoulder to bottom hem.','M250 45V166M246 45H254M246 166H254',262,109);
 add(shoulder,'Shoulder width','Shoulder seam to shoulder seam.','M96 20H184M96 16V24M184 16V24',140,12);
 if (!tank) add(sleeve,'Sleeve length','Shoulder seam to sleeve edge.',longSleeve ? 'M82 46 45 164M78 45 86 47M41 163 49 165' : 'M85 42 53 80M82 39 88 45M50 77 56 83',longSleeve ? 49 : 56,longSleeve ? 106 : 51);
 if (measurements.length < 2) return null;
 const outline = tank
  ? 'M108 36 122 33Q140 56 158 33L172 36Q167 75 184 88L184 166Q140 171 96 166L96 88Q113 75 108 36Z'
  : longSleeve
   ? 'M96 45 122 34Q140 44 158 34L184 45 202 61 234 166 215 173 184 95 184 166Q140 171 96 166L96 95 65 173 46 166 78 61Z'
   : 'M96 45 122 34Q140 44 158 34L184 45 220 75 206 99 184 87 184 166Q140 171 96 166L96 87 74 99 60 75Z';
 const seams = tank
  ? 'M112 37Q116 75 100 90M168 37Q164 75 180 90M122 38Q140 62 158 38'
  : longSleeve
   ? 'M122 34Q140 63 158 34M123 38Q140 67 157 38M96 45Q102 71 96 95M184 45Q178 71 184 95M48 160 67 167M213 167 232 160'
   : 'M122 34Q140 63 158 34M123 38Q140 67 157 38M96 45Q102 65 96 87M184 45Q178 65 184 87M64 72 78 96M216 72 202 96';
 const help = document.createElement('div');
 help.className = 'lr-measure-help';
 help.innerHTML = '<h3>How to measure</h3><div class="lr-measure-help-layout"><svg viewBox="24 0 256 184" role="img" aria-label="' + (tank ? 'Running singlet' : longSleeve ? 'Running long sleeve' : 'Running T-shirt') + ' measurement diagram"><path class="lr-measure-garment" d="' + outline + '"/><path class="lr-measure-seams" d="' + seams + 'M97 160Q140 165 183 160"/><g class="lr-measure-lines">' + measurements.map(item => '<path d="' + item.line + '"/>').join('') + '</g><g class="lr-measure-letters">' + measurements.map(item => '<text x="' + item.x + '" y="' + item.y + '" text-anchor="middle">' + item.letter + '</text>').join('') + '</g></svg><dl>' + measurements.map(item => '<div><dt>' + item.letter + ' · ' + item.name + '</dt><dd>' + item.definition + '</dd></div>').join('') + '</dl></div>';
 return help;
}

/* Product-owned measurements remain editable in Shopify descriptions. */
class LievreProductDescription extends HTMLElement {
 connectedCallback() {
  this.classList.add('lr-description-ready');
  this.querySelectorAll('.model-info, .detail-content p').forEach(element => {
   if (!element.matches('.model-info') && !/model/i.test(element.textContent)) return;
   const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
   let node;
   while ((node = walker.nextNode())) {
    node.textContent = node.textContent.replace(/(\d+(?:[.,]\d+)?)\s*cm\b(?!\s*\()/gi, (match, value) => {
     const height = Number(value.replace(',', '.'));
     if (height < 100 || height > 230) return match;
     const inches = Math.round(height / 2.54);
     return match + ' (' + Math.floor(inches / 12) + '′' + inches % 12 + '″)';
    });
   }
  });
  // Keep fit context directly below the native size choices, outside size help.
  const details = [...this.querySelectorAll('details')];
  const sizeDetails = details.filter(item => /size\s*(guide|chart)/i.test(item.querySelector('summary')?.textContent || ''));
  const candidates = [...this.querySelectorAll('.model-info'),...sizeDetails.flatMap(item => [...item.querySelectorAll('p')].filter(p => /\bmodel\b/i.test(p.textContent)))];
  const models = [...new Set(candidates)].filter(node => !candidates.some(other => other !== node && other.contains(node)));
  let modelAnchor = this.closest('product-info')?.querySelector('variant-selects');
  models.forEach(model => {
   model.classList.add('lr-model-note');
   if (modelAnchor) { modelAnchor.after(model); modelAnchor = model; }
   else this.append(model);
  });
  const productTitle = this.closest('product-info')?.querySelector('h1')?.textContent || '';
  const accessory = /bandana|gavroche|scarf/i.test(productTitle);
  if (accessory) {
   this.querySelectorAll('details').forEach(detail => {
    const summary = detail.querySelector('summary');
    if (!/dimensions?|size\s*(guide|chart)/i.test(summary?.textContent || '') || detail.querySelector('table')) return;
    const content = detail.querySelector('.detail-content');
    const dimension = [...(content?.querySelectorAll('p') || [])].find(p => /(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*cm\b/i.test(p.textContent));
    const match = dimension?.textContent.match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*cm\b/i);
    if (!match) return;
    summary.textContent = 'Size Guide';
    const table = document.createElement('table');
    table.className = 'size-table';
    const width = Number(match[1].replace(',','.')), length = Number(match[2].replace(',','.'));
    table.innerHTML = '<thead><tr><th>Size</th><th>Width (cm)</th><th>Length (cm)</th></tr></thead><tbody><tr><td>One size</td><td>' + width + '</td><td>' + length + '</td></tr></tbody>';
    dimension.replaceWith(table);
    content.querySelectorAll('p').forEach(note => {
     if (!/dimensions indicated may vary slightly/i.test(note.textContent)) return;
     note.textContent = 'As each gavroche is made from soft 100% cotton fabric, the dimensions indicated may vary slightly.';
     note.classList.add('lr-accessory-size-note');
    });
   });
  }
  this.querySelectorAll('table').forEach(table => {
   if (table.dataset.lrSizeReady) return;
   const guide = table.closest('details');
   if (!table.classList.contains('size-table') && !/size (guide|chart)/i.test(guide?.querySelector('summary')?.textContent || '')) return;
   const explicitCm = /\bcm\b|centimet/i.test(table.textContent);
   if (!explicitCm) return;
   const cells = [...table.querySelectorAll('tbody tr')].flatMap(row => [...row.cells].slice(1))
    .map(cell => {
     const match = cell.textContent.trim().match(/^(\d+(?:[.,]\d+)?)(?:\s*cm)?$/i);
     return match ? {cell, cm:Number(match[1].replace(',', '.'))} : null;
    }).filter(Boolean);
   if (!cells.length) return;
   table.dataset.lrSizeReady = 'true';
   table.classList.add('lr-size-table');
   table.style.setProperty('--lr-chart-columns', table.rows[0]?.cells.length || 3);
   table.querySelectorAll('thead th').forEach(cell => {
    cell.setAttribute('scope','col');
    cell.textContent = cell.textContent.replace(/\s*\(?cm\)?\s*$/i,'');
   });
   table.querySelectorAll('tbody tr').forEach(row => {
    const cell = row.cells[0];
    if (cell?.tagName !== 'TD') return;
    const heading = document.createElement('th');
    heading.scope = 'row'; heading.textContent = cell.textContent;
    cell.replaceWith(heading);
   });
   const wrapper = document.createElement('div');
   wrapper.className = 'lr-size-guide';
   const toolbar = document.createElement('div');
   toolbar.className = 'lr-size-toolbar';
   const label = document.createElement('span');
   label.textContent = accessory ? 'Dimensions' : 'Garment measurements';
   const controls = document.createElement('div');
   controls.className = 'lr-size-units'; controls.setAttribute('role','group');
   controls.setAttribute('aria-label','Measurement units');
   const viewport = document.createElement('div');
   viewport.className = 'lr-size-scroll'; viewport.tabIndex = 0;
   viewport.setAttribute('role','region'); viewport.setAttribute('aria-label','Size guide');
   const status = document.createElement('span');
   status.className = 'visually-hidden'; status.setAttribute('role','status');
   status.setAttribute('aria-live','polite');
   const buttons = ['cm','in'].map(unit => {
    const button = document.createElement('button');
    button.type = 'button'; button.textContent = unit.toUpperCase();
    button.dataset.sizeUnit = unit;
    button.setAttribute('aria-label','Show measurements in ' + (unit === 'cm' ? 'centimetres' : 'inches'));
    controls.append(button);
    return button;
   });
   const update = unit => {
    cells.forEach(({cell,cm}) => { cell.textContent = unit === 'cm' ? String(cm) : (cm / 2.54).toFixed(1); });
    buttons.forEach(button => button.setAttribute('aria-pressed',String(button.dataset.sizeUnit === unit)));
    const name = unit === 'cm' ? 'centimetres' : 'inches';
    table.setAttribute('aria-label','Garment measurements in ' + name);
    status.textContent = 'Measurements shown in ' + name + '.';
   };
   controls.addEventListener('click',event => {
    const button = event.target.closest('[data-size-unit]');
    if (button) update(button.dataset.sizeUnit);
   });
   toolbar.append(label,controls);
   table.before(wrapper);
   viewport.append(table);
   wrapper.append(toolbar,viewport,status);
   update('cm');
   const chart = lievreReadSizeChart(table, cells);
   const help = lievreMeasurementHelp(table,this.closest('product-info'));
   const explanation = document.createElement('p');
   explanation.className = 'lr-chart-intro';
   explanation.textContent = 'Measured flat.';
   explanation.hidden = accessory;
   wrapper.insertBefore(explanation,toolbar);
   if (help) wrapper.append(help);
   if (chart.length > 1) {
    const choices = document.createElement('div');
    choices.className = 'lr-guide-choices';
    choices.setAttribute('role','group');
    choices.setAttribute('aria-label','Size help');
    const measurements = document.createElement('div');
    measurements.className = 'lr-guide-panel';
    measurements.id = 'lr-measurements-' + (++LievreProductDescription.guideCount);
    measurements.hidden = false;
    measurements.append(explanation,toolbar,viewport,status);
    if (help) measurements.append(help);
    const finder = document.createElement('lr-size-finder');
    finder.chart = chart;
    finder.className = 'lr-guide-panel';
    finder.id = 'lr-recommendation-' + LievreProductDescription.guideCount;
    finder.hidden = true;
    const panels = [measurements,finder];
    const labels = ['Size guide','Find your size'];
    const selectors = panels.map((panel,index) => {
     const button = document.createElement('button');
     button.type = 'button';
     button.textContent = labels[index];
     button.setAttribute('aria-controls',panel.id);
     button.setAttribute('aria-expanded',String(index === 0));
     button.addEventListener('click',() => {
      panels.forEach(item => { item.hidden = item !== panel; });
      selectors.forEach(item => item.setAttribute('aria-expanded',String(item === button)));
     });
     choices.append(button);
     return button;
    });
    wrapper.append(choices,measurements,finder);
   }
  });
  this.closest('product-info')?.querySelector('lr-size-drawer')?.mount(this);
 }
}
LievreProductDescription.guideCount = 0;
if (!customElements.get('lr-product-description')) customElements.define('lr-product-description',LievreProductDescription);



